/**
 * Introduction API service for HTTP requests to backend
 *
 * Centralizes all introduction-related API calls with consistent error handling
 * and TypeScript types.
 *
 * @module introductionApi
 */

import { apiDelete, apiGet, apiPost, apiPut } from '@/lib/apiUtils';

/**
 * Introduction interface matching backend structure
 */
export interface Introduction {
  id: string;
  title: string;
  subtitle: string;
  text: string;
  material_type: 'text' | 'image' | 'audio' | null;
  material_url: string | null;
  material_binary: string | null;
  material_extension: string | null;
  material_mime_type: string | null;
  duration: number | null;
  module: 'reading' | 'listening' | 'writing' | 'speaking';
  exam_type: string[];
  level: string;
  created_at: string;
  updated_at: string;
}

/**
 * Introduction form data for create/update operations
 */
export interface IntroductionFormData {
  title: string;
  subtitle?: string;
  text?: string;
  material_type?: 'text' | 'image' | 'audio' | null;
  duration?: number | null;
  module: 'reading' | 'listening' | 'writing' | 'speaking';
  exam_type: string[];
  level: string;
  material_base64?: string;
  material_extension?: string;
  material_mime_type?: string;
}

/**
 * Get lightweight list of introductions with metadata only
 *
 * @returns {Promise<Introduction[]>} Array of introduction objects with metadata
 * @throws {Error} If API request fails
 */
export const getIntroductionsList = async (): Promise<Introduction[]> => {
  const result = await apiGet<Introduction[]>('/admin/introductions/list', {
    errorMessage: 'Failed to fetch introductions',
    context: 'getIntroductionsList',
    throwOnError: true,
  });
  if (!result) {
    throw new Error('Failed to fetch introductions');
  }
  return result;
};

/**
 * Get single introduction with full content including media
 *
 * @param {string} id - Introduction ID
 * @returns {Promise<Introduction>} Full introduction object with binary content
 * @throws {Error} If API request fails
 */
export const getIntroductionFull = async (
  id: string,
): Promise<Introduction> => {
  const result = await apiGet<Introduction>(`/admin/introductions/${id}/full`, {
    errorMessage: 'Failed to fetch introduction details',
    context: 'getIntroductionFull',
    throwOnError: true,
  });
  if (!result) {
    throw new Error('Failed to fetch introduction details');
  }
  return result;
};

/**
 * Create a new introduction
 *
 * @param {IntroductionFormData} introData - Introduction data to create
 * @returns {Promise<Introduction>} Created introduction object
 * @throws {Error} If API request fails
 */
export const createIntroduction = async (
  introData: IntroductionFormData,
): Promise<Introduction> => {
  const result = await apiPost<Introduction>('/admin/introductions', introData, {
    errorMessage: 'Failed to create introduction',
    context: 'createIntroduction',
    throwOnError: true,
  });
  if (!result) {
    throw new Error('Failed to create introduction');
  }
  return result;
};

/**
 * Update an existing introduction
 *
 * @param {string} id - Introduction ID to update
 * @param {IntroductionFormData} introData - Updated introduction data
 * @returns {Promise<Introduction>} Updated introduction object
 * @throws {Error} If API request fails
 */
export const updateIntroduction = async (
  id: string,
  introData: IntroductionFormData,
): Promise<Introduction> => {
  const result = await apiPut<Introduction>(
    `/admin/introductions/${id}`,
    introData,
    {
      errorMessage: 'Failed to update introduction',
      context: 'updateIntroduction',
      throwOnError: true,
    },
  );
  if (!result) {
    throw new Error('Failed to update introduction');
  }
  return result;
};

/**
 * Delete an introduction by ID
 *
 * @param {string} id - Introduction ID to delete
 * @returns {Promise<void>} Resolves on successful deletion
 * @throws {Error} If API request fails
 */
export const deleteIntroduction = async (id: string): Promise<void> => {
  await apiDelete(`/admin/introductions/${id}`, {
    errorMessage: 'Failed to delete introduction',
    context: 'deleteIntroduction',
    throwOnError: true,
  });
};

/**
 * Get introduction for simulation by module, exam type, and level
 *
 * @param {string} module - Module name (e.g., "listening", "reading")
 * @param {string} examType - Exam type (e.g., "goethe", "osd", "telc")
 * @param {string | null} level - Optional CEFR level (A1, A2, B1, B2, C1, C2)
 * @returns {Promise<Introduction | null>} Introduction object or null if not found
 * @throws {Error} If API request fails
 */
export const getIntroductionForSimulation = async (
  module: string,
  examType: string,
  level?: string | null,
): Promise<Introduction | null> => {
  const params = new URLSearchParams({
    module,
    exam_type: examType,
  });
  if (level) {
    params.append('level', level);
  }

  const data = await apiGet<Introduction>(
    `/admin/introductions/for-simulation?${params.toString()}`,
    {
      errorMessage: 'Failed to fetch introduction for simulation',
      context: 'getIntroductionForSimulation',
      throwOnError: true,
    },
  );
  return data?.id ? data : null;
};
